import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { CardPlugin } from '@atlaskit/editor-plugin-card';
import type { FocusPlugin } from '@atlaskit/editor-plugin-focus';
import type { GridPlugin } from '@atlaskit/editor-plugin-grid';
import type { HyperlinkPlugin } from '@atlaskit/editor-plugin-hyperlink';
import type { HyperlinkPluginSharedState } from '@atlaskit/editor-plugin-hyperlink/hyperlinkPluginType';
import type { ListPlugin } from '@atlaskit/editor-plugin-list';
import type { MaxContentSizePlugin } from '@atlaskit/editor-plugin-max-content-size';
import type { MentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import type { PlaceholderPlugin } from '@atlaskit/editor-plugin-placeholder';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import type { SaveOnEnterPlugin } from '@atlaskit/editor-plugin-save-on-enter';
import type { TypeAheadPlugin } from '@atlaskit/editor-plugin-type-ahead';
import { type MarkType } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';

import { useRenderStabilizedCallback } from './useRenderStabilizedCallback';

type UseLinkTypeAheadParams = {
	editorApi:
		| PublicPluginAPI<
				[
					CardPlugin,
					SaveOnEnterPlugin,
					MaxContentSizePlugin,
					MentionsPlugin,
					GridPlugin,
					QuickInsertPlugin,
					ListPlugin,
					TypeAheadPlugin,
					HyperlinkPlugin,
					FocusPlugin,
					PlaceholderPlugin,
				]
		  >
		| undefined;
	closeTypeAhead: () => void;
	isRenderStabilizationEnabled: boolean;
};

export const useLinkTypeAhead = ({
	editorApi,
	closeTypeAhead,
	isRenderStabilizationEnabled,
}: UseLinkTypeAheadParams): {
	openLinkTypeAheadFromMenu: () => void;
	openLinkTypeAheadFromPlaceholder: () => void;
} => {
	const openLinkTypeAheadFromMenu = useRenderStabilizedCallback(
		(): void => {
			closeTypeAhead();

			const command = editorApi?.hyperlink?.commands.showLinkToolbar(INPUT_METHOD.QUICK_INSERT);
			editorApi?.core.actions.execute(command);
		},
		[closeTypeAhead, editorApi],
		isRenderStabilizationEnabled,
	);

	const openLinkTypeAheadFromPlaceholder = useRenderStabilizedCallback(
		(): void => {
			closeTypeAhead();

			let selectionRange = { from: 0, to: 0 };
			let placeholderMark: MarkType | null = null;

			// removes placeholder marks so that toolbar can be created
			editorApi?.core.actions.execute(({ tr }: { tr: Transaction }) => {
				const { from, to } = tr.selection;
				selectionRange = { from, to };
				placeholderMark = tr.doc.type.schema.marks.code;

				tr.removeMark(from, to, placeholderMark);

				return tr;
			});

			// open link toolbar
			const command = editorApi?.hyperlink?.commands.showLinkToolbar(INPUT_METHOD.QUICK_INSERT);
			editorApi?.core.actions.execute(command);

			// track toolbar state to restore placeholder marks when toolbar closes
			const unsubscribe = editorApi?.hyperlink?.sharedState.onChange(
				({ nextSharedState }: { nextSharedState: HyperlinkPluginSharedState }) => {
					if (!nextSharedState?.activeLinkMark) {
						editorApi?.core.actions.execute(({ tr }: { tr: Transaction }) => {
							const { from, to } = tr.selection;
							const linkMark = tr.doc.type.schema.marks.link;

							const hasLinkInOriginalRange = tr.doc.rangeHasMark(
								selectionRange.from,
								selectionRange.to,
								linkMark,
							);
							const hasLinkInCurrentSelection = tr.doc.rangeHasMark(from, to, linkMark);

							// restore placeholder marks if no link was created
							if (placeholderMark && !hasLinkInOriginalRange && !hasLinkInCurrentSelection) {
								tr.addMark(selectionRange.from, selectionRange.to, placeholderMark.create());
							}

							return tr;
						});

						unsubscribe?.();
					}
				},
			);
		},
		[closeTypeAhead, editorApi],
		isRenderStabilizationEnabled,
	);

	return {
		openLinkTypeAheadFromMenu,
		openLinkTypeAheadFromPlaceholder,
	};
};
